import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Box } from "@material-ui/core";
import Loading from 'src/components/Loading';

const LineChartCard = ({
  data,
  isLoading,
  error
}) => {
  // Get the minimum and maximum date from the data
  const minDate = new Date(Math.min(...data.map((item) => new Date(item.date))));
  const maxDate = new Date(Math.max(...data.map((item) => new Date(item.date))));

  // Calculate the difference in days between the minimum and maximum date
  const dateDifferenceInDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));

  // Determine the number of days to display (at least 14 days)
  const numberOfDaysToDisplay = Math.max(dateDifferenceInDays, 14);

  // Slice the data to display the last `numberOfDaysToDisplay` days
  const slicedData = data.slice(-numberOfDaysToDisplay);

  // Define a set of 50 constant visible colors
  const constantVisibleColors = [
    'rgb(255, 99, 132)',
    'rgb(54, 162, 235)',
    'rgb(255, 205, 86)',
    'rgb(75, 192, 192)',
    'rgb(153, 102, 255)',
    'rgb(255, 159, 64)',
    'rgb(255, 102, 102)',
    'rgb(255, 204, 102)',
    'rgb(102, 255, 102)',
    'rgb(102, 102, 255)',
    'rgb(255, 51, 51)',
    'rgb(255, 153, 51)',
    'rgb(0, 255, 0)',
    'rgb(0, 0, 255)',
    'rgb(255, 51, 153)',
    'rgb(255, 153, 255)',
    'rgb(51, 153, 255)',
    'rgb(204, 0, 0)',
    'rgb(204, 102, 0)',
    'rgb(0, 204, 0)',
    'rgb(0, 0, 204)',
    'rgb(204, 51, 102)',
    'rgb(204, 102, 153)',
    'rgb(102, 204, 153)',
    'rgb(153, 0, 0)',
    'rgb(153, 76, 0)',
    'rgb(0, 153, 0)',
    'rgb(0, 0, 153)',
    'rgb(153, 38, 76)',
    'rgb(153, 76, 114)',
    'rgb(76, 153, 114)',
    'rgb(102, 0, 0)',
    'rgb(102, 51, 0)',
    'rgb(0, 102, 0)',
    'rgb(0, 0, 102)',
    'rgb(102, 26, 51)',
    'rgb(102, 51, 76)',
    'rgb(51, 102, 76)',
    'rgb(51, 0, 0)',
    'rgb(51, 25, 0)',
    'rgb(0, 51, 0)',
    'rgb(0, 0, 51)',
    'rgb(51, 0, 102)',
    'rgb(51, 0, 51)',
    'rgb(51, 51, 0)',
    'rgb(0, 51, 102)',
    'rgb(0, 51, 51)',
    'rgb(0, 102, 51)',
  ];

  // Generate the x-axis labels
  const generateXAxisLabels = () => {
    const labels = slicedData.map((item) => {
      const date = new Date(item.date);
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'short' });
      return `${day} ${month}`;
    });
    return labels;
  };

  // Generate the datasets for line chart
  const generateLineChartDatasets = () => {
    const lineDatasets = [];
    const barDatasets = [];

    // Extract company names from the first date object
    const companyNames = new Set();

    slicedData.forEach((item) => {
      // Iterate through keys in item and add company names to the set
      Object.keys(item).forEach((key) => {
        if (key !== 'date') {
          companyNames.add(key);
        }
      });
    });

    const getNextVisibleColor = (() => {
      let index = 0;

      return () => {
        index = (index + 1) % constantVisibleColors.length;
        return constantVisibleColors[index];
      };
    })();

    const uniqueCompanyNames = Array.from(companyNames);

    uniqueCompanyNames.forEach((companyName, index) => {
      const values = slicedData.map((item) => item[companyName]);

      if (companyName.endsWith('_total_count')) {
        const label = companyName.replace('_total_count', '');

        lineDatasets.push({
          type: 'line',
          label: `${label} Total Scan Count`,
          data: values,
          fill: false,
          borderColor: getNextVisibleColor(),
          tension: 0.1,
        });
      } else if (companyName.endsWith('_total_suspend')) {
        const label = companyName.replace('_total_suspend', '');

        barDatasets.push({
          type: 'bar',
          label: `${label} Total Suspended Count`,
          data: values,
          backgroundColor: getNextVisibleColor(),
          stack: 'stack1',
          yAxisID: 'suspended-y-axis',
        });
      }
    });

    const datasets = [...lineDatasets, ...barDatasets];
    return datasets;
  };

  const combinedChartData = {
    labels: generateXAxisLabels(),
    datasets: generateLineChartDatasets(),
  };

  const chartOptions = {
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: 'Scan Count',
        },
        grid: {
          borderColor: 'rgb(75, 192, 192)',
        },
      },
      'suspended-y-axis': {
        position: 'right',
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: 'Suspended Count',
        },
        grid: {
          borderColor: 'rgb(255, 99, 132)',
        },
      },
      x: {
        title: {
          min: 5,
          display: true,
          text: 'Date',
        },
        grid: {
          borderColor: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      htmlLegend: {
        containerID: 'legend-container',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        filter: (tooltipItem) => tooltipItem.raw > 0,
      },
    },
  };

  const getOrCreateLegendList = (chart, id) => {
    const legendContainer = document.getElementById(id);
    let listContainer = legendContainer.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.display = 'flex';
      listContainer.style.flexDirection = 'column';
      listContainer.style.margin = 0;
      listContainer.style.marginLeft = '16px';
      listContainer.style.padding = 0;

      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  };

  const htmlLegendPlugin = {
    id: 'htmlLegend',
    afterUpdate(chart, args, options) {
      const ul = getOrCreateLegendList(chart, options.containerID);

      // Remove old legend items
      while (ul.firstChild) {
        ul.firstChild.remove();
      }

      // Reuse the built-in legendItems generator
      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      items.forEach((item) => {
        const li = document.createElement('li');
        li.style.alignItems = 'center';
        li.style.cursor = 'pointer';
        li.style.display = 'flex';
        li.style.flexDirection = 'row';
        li.style.marginTop = '10px';

        li.onclick = () => {
          const { type } = chart.config;
          if (type === 'pie' || type === 'doughnut') {
            // Pie and doughnut charts only have a single dataset and visibility is per item
            chart.toggleDataVisibility(item.index);
          } else {
            chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
          }
          chart.update();
        };

        // Color box
        const boxSpan = document.createElement('span');
        boxSpan.style.background = item.fillStyle;
        boxSpan.style.borderColor = item.strokeStyle;
        boxSpan.style.borderStyle = 'solid';
        boxSpan.style.borderWidth = '3px';
        boxSpan.style.display = 'inline-block';
        boxSpan.style.flexShrink = 0;
        boxSpan.style.height = '20px';
        boxSpan.style.marginRight = '10px';
        boxSpan.style.width = '20px';

        // Text
        const textContainer = document.createElement('p');
        textContainer.style.color = item.fontColor;
        textContainer.style.margin = 0;
        textContainer.style.padding = 0;
        textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

        const text = document.createTextNode(item.text);
        textContainer.appendChild(text);

        li.appendChild(boxSpan);
        li.appendChild(textContainer);
        ul.appendChild(li);
      });
    }
  };

  const renderContent = () => {
    if (isLoading) return <div style={{ height: '100%', width: '100%', display: 'grid', placeItems: 'center' }}><Loading /></div>;
    if (error) return <div style={{ height: '100%', width: '100%', display: 'grid', placeItems: 'center' }}>Error: {error}</div>;
    if (!data.length) return <div style={{ height: '100%', width: '100%', display: 'grid', placeItems: 'center' }}>No data found</div>;
    return (
      <>
        <Box flexGrow={1} height="100%">
          <Line data={combinedChartData} options={chartOptions} plugins={[htmlLegendPlugin]} />
        </Box>
        <Box id="legend-container" style={{ width: "300px", height: "100%", overflowY: "auto" }} />
      </>
    );
  };

  return (
    <Box>
      <h3 style={{ fontSize: '24px', fontWeight: 'bolder' }}>Total Scan Count and Suspended Count</h3>
      <Box style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '480px' }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default LineChartCard;
